import { useQuery } from '@tanstack/react-query';
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { apiClient } from '@/core/http/client';
import { usePrincipal } from '@/core/auth/appToken';
import { SUPPORT_DASHBOARD_ALLOWED_ROLES } from '@/models/role';
import type { ApiError } from '@/models/apiError';
import type { components } from '@/generated/api/schema';

/**
 * Contact record of the partner DELABO lab assigned to the caller's own organization —
 * name/street/zip/city/phone, all but `id`/`name` optional depending on what is maintained.
 */
export type PartnerLabCurrentResponse = components['schemas']['PartnerLabCurrentResponse'];

/** Cache key for the caller's current partner lab — no parameters, single fixed key. */
export const currentPartnerLabKey = ['partner-lab', 'current'] as const;

/**
 * The partner lab is chosen once during onboarding and afterwards changed only through a
 * dedicated support process — it is Stammdaten, not a live state that needs near-real-time
 * refresh. A 15-minute `staleTime` avoids re-fetching this on every mount of the header and the
 * profile card (which share this one cache entry) while staying well within the bound at which a
 * support-driven reassignment would realistically need to show up unprompted.
 *
 * This override is set **locally on this hook**, not globally: the `QueryProvider` default
 * (`staleTime: 30_000`) is intentionally left untouched because it is correct for order
 * lists/details, which must reflect lab-side status changes within seconds. Overriding it here
 * only affects this one query, never other TanStack Query consumers.
 */
const PARTNER_LAB_STALE_TIME_MS = 15 * 60_000;

/**
 * `gcTime` must exceed `staleTime`, or the cache entry is garbage-collected (once unobserved)
 * before it goes stale, silently defeating the longer `staleTime` above. The global default
 * `gcTime` (5 minutes) is shorter than `PARTNER_LAB_STALE_TIME_MS`, so it must be overridden here
 * too.
 */
const PARTNER_LAB_GC_TIME_MS = 30 * 60_000;

interface UseCurrentPartnerLabOptions {
  /**
   * Gates the query, e.g. on the caller's org type — callers that never have a partner-lab
   * relation (DELABO-internal contexts) should not fire the request at all. Defaults to `true`.
   * Combined (AND) with the hook's own {@link SUPPORT_DASHBOARD_ALLOWED_ROLES} exclusion below —
   * callers do not need to repeat that check themselves.
   */
  readonly enabled?: boolean;
}

async function fetchCurrentPartnerLab(): Promise<PartnerLabCurrentResponse | null> {
  // The owning organization is resolved exclusively from the caller's ActorContext on the
  // server side — this call never sends a tenant/organization id (prevents a tenant leak via
  // a spoofed parameter). A `204 No Content` (partner-lab selection still pending during
  // onboarding) yields no body, so `data` is `undefined` — that is a valid "no data" state, not
  // an error, and must not be conflated with a non-2xx response (which becomes a typed
  // `ApiError` before this line is reached — see client.ts).
  const { data } = await apiClient.GET('/api/v1/partner-lab/current');
  return data ?? null;
}

/**
 * Fetches the partner DELABO lab currently assigned to the caller's own organization — shared by
 * the application header (`PartnerLabInfo`) and the own-profile page's "Mein Partnerlabor" card
 * (`PartnerLabCard`), both of which read the same single cache entry rather than firing two
 * separate requests for the same data. Resolves to `null` while no partner lab is assigned yet
 * (onboarding pending) — never treated as an error.
 *
 * Never fires for a {@link SUPPORT_DASHBOARD_ALLOWED_ROLES} actor, regardless of the caller's own
 * `enabled` option: a Support/Admin actor whose membership happens to sit under a
 * `PRACTICE`/`CUSTOMER_LAB` organization (e.g. local/dev test data reusing an existing org instead
 * of a dedicated `DELABO_PORTAL` one) must never see another tenant's partner-lab contact data.
 * Enforcing this exclusion here, not per-consumer, is what makes it binding for both current
 * consumers and any future one — a consumer forgetting to repeat an org-type check would otherwise
 * populate the shared cache entry for the other, less-guarded consumer to read. Fail-closed: no
 * principal resolved yet also means no request.
 */
export function useCurrentPartnerLab(
  options: UseCurrentPartnerLabOptions = {},
): UseQueryResult<PartnerLabCurrentResponse | null, ApiError> {
  const principal = usePrincipal();
  const isSupportRole = principal !== null && SUPPORT_DASHBOARD_ALLOWED_ROLES.includes(principal.role);

  const queryOptions: UseQueryOptions<PartnerLabCurrentResponse | null, ApiError> = {
    queryKey: currentPartnerLabKey,
    queryFn: fetchCurrentPartnerLab,
    enabled: (options.enabled ?? true) && !isSupportRole,
    staleTime: PARTNER_LAB_STALE_TIME_MS,
    gcTime: PARTNER_LAB_GC_TIME_MS,
  };
  return useQuery(queryOptions);
}
